// D9-03 (#408) — the Zod mirror of the Store/UpdateWorkOrderRequest FormRequest
// bounds (apps/tenant/app/Modules/Service/Requests/{Store,Update}WorkOrderRequest.php).
// This is a UX convenience only: it lets the create/edit Sheet show inline
// errors before a round trip, never a substitute for the server's own
// validation, which remains the sole authority.
//
// NEITHER schema below declares a `state` key. D7-01 (#408) marks it
// `prohibited` on every write body — `UpdateWorkOrderRequest::rules()`'s
// `'state' => ['prohibited']` 422s the instant that key is present at all,
// regardless of value — so the client must never send one. The omission
// here is deliberate, not an oversight (RESEARCH Pitfall 2).
import { z } from 'zod';

const AUTHORISATION_METHODS = ['in_person', 'phone', 'email', 'portal'] as const;

/**
 * RESEARCH Pitfall 4 — `authorised_amount` is the one work-order money field
 * that is DECIMAL DOLLARS (contract `Format: float`), not integer cents like
 * every other Service money field. It mirrors the FormRequest's own
 * `numeric|min:0|max:9999999999.99` rule exactly. Keep this field on a plain
 * decimal `Input type="number" step="0.01"` — routing it through the
 * module's cents-based controlled money input would silently multiply every
 * value entered here by 100.
 */
const authorisedAmountSchema = z.preprocess((value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}, z.number().min(0).max(9999999999.99).nullable().optional());

/**
 * `dropoff_date`/`promised_date` are bound to `Input type="date"` controls,
 * which read/write plain `YYYY-MM-DD` strings — never a full timestamp. The
 * Store/UpdateWorkOrderRequest `date` rule accepts that literal string form
 * (Laravel's `date` validator is not format-strict), so the Sheet submits it
 * as-is with no time component appended and no widening to a full
 * `date-time` string before the request leaves the browser.
 */
const dateOnlySchema = z.preprocess(
  (value) => (value === '' || value === undefined ? null : value),
  z.string().nullable().optional(),
);

export const createWorkOrderSchema = z.object({
  customer_id: z.string().min(1, 'Customer is required'),
  boat_id: z.string().nullable().optional(),
  location_id: z.string().nullable().optional(),
  service_advisor_id: z.string().nullable().optional(),
  dropoff_date: dateOnlySchema,
  promised_date: dateOnlySchema,
  authorised_amount: authorisedAmountSchema,
  authorisation_method: z.enum(AUTHORISATION_METHODS).nullable().optional(),
  // DEP-02 / D12-05 (#408) — the Zod mirror of the FormRequest's
  // `['nullable', 'integer', 'min:0']`; integer CENTS, unlike
  // `authorised_amount` two lines above, which is decimal dollars — routing
  // this one through the decimal treatment (or that one through
  // `MoneyInput`) silently multiplies or divides by 100.
  deposit_required_cents: z.number().int().min(0).nullable().optional(),
  // UI-only (D9-03) — drives the D9-02 post-create chain: ON runs
  // confirm-then-check-in and stamps today's dealer-local `dropoff_date`; OFF
  // runs confirm only and reveals an "Expected drop-off" date input. Never
  // sent to the API — `WorkOrderFormSheet` strips this key before building
  // the `CreateWorkOrderRequest` payload.
  boat_on_site: z.boolean().default(true),
});

export type CreateWorkOrderFormData = z.infer<typeof createWorkOrderSchema>;

export const updateWorkOrderSchema = z.object({
  customer_id: z.string().min(1, 'Customer is required').optional(),
  boat_id: z.string().nullable().optional(),
  // Unlike create, the contract's UpdateWorkOrderRequest#location_id is a
  // plain (non-nullable) `sometimes` string — PATCH can change the location
  // but never null it out.
  location_id: z.string().min(1).optional(),
  service_advisor_id: z.string().nullable().optional(),
  dropoff_date: dateOnlySchema,
  promised_date: dateOnlySchema,
  authorised_amount: authorisedAmountSchema,
  authorisation_method: z.enum(AUTHORISATION_METHODS).nullable().optional(),
  // DEP-02 / D12-05 (#408) — the Zod mirror of the FormRequest's
  // `['nullable', 'integer', 'min:0']`; integer CENTS, unlike
  // `authorised_amount` two lines above, which is decimal dollars — routing
  // this one through the decimal treatment (or that one through
  // `MoneyInput`) silently multiplies or divides by 100.
  deposit_required_cents: z.number().int().min(0).nullable().optional(),
  // NO `state` key here either — see the file header. D7-01's `prohibited`
  // rule applies to every PATCH regardless of caller.
});

export type UpdateWorkOrderFormData = z.infer<typeof updateWorkOrderSchema>;
